import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import { Typeahead } from 'react-bootstrap-typeahead'
import { Alert, ButtonToolbar, ToggleButtonGroup, ToggleButton, Button, Grid } from 'react-bootstrap'
import axios from 'axios'
import swal from 'sweetalert'
import './style.css'
import 'react-table/react-table.css'
import './bootstrap-table.css'

import { resetInputtedHours, successGetEmployeeProjectHours, successGetEmployeeProposalHours, addLastWeekHours, addNewHours, addHours } from './reducers/manageHours'

import { handleErrorFetchingData } from '../../functions/errorHandling'
import getServerURL from '../../functions/getServerURL'

import AuthenticationComponent from '../../AuthenticationComponent';

import WeekPicker from './components/WeekPicker'
import WorkloadTrackerTable from './components/WorkloadTrackerTable'


const isThisWeekReadOnly = (firstDayOfSelectedWeek) => {
  const now = new Date()

  const firstDayOfThisWeek = now.getDate() - now.getDay() + 1
  const firstDateOfThisWeek = new Date()
  firstDateOfThisWeek.setDate(firstDayOfThisWeek)

  const firstDateOfSelectedWeek = new Date(firstDayOfSelectedWeek)

  if (firstDayOfSelectedWeek === firstDateOfThisWeek.toLocaleDateString('en-Us') || firstDateOfSelectedWeek > now) {
    return false
  }

  const firstDayOfLastWeek = now.getDate() - now.getDay() - 6
  const firstDateOfLastWeek = new Date()
  firstDateOfLastWeek.setDate(firstDayOfLastWeek)

  const selectedWeekIsOneWeekPriorToCurrentWeek = firstDayOfSelectedWeek === firstDateOfLastWeek.toLocaleDateString('en-Us')
  
  if (now.getDay() === 0 && selectedWeekIsOneWeekPriorToCurrentWeek) {
    return false
  }

  if (now.getDay() === 1 && selectedWeekIsOneWeekPriorToCurrentWeek) {
    const nowWithTimeZone = now.toLocaleString('en-Us', {
      timeZone: 'America/New_York',
      hour12: false,
    })

    const currentTime = nowWithTimeZone.split(', ')[1]
    const currentHour = currentTime ? currentTime.split(':')[0] : NaN
    const currentHourAsNumber = Number(currentHour)
    if (currentHourAsNumber && isNaN(currentHourAsNumber) === false && currentHourAsNumber < 12) {
      return false
    }
  }

  return true
}


class WorkloadContainer extends Component {
  constructor(props) {
    super(props)

    // get current date
    const date = new Date()

    // First day is the day of the month - (the day of the week + 1 to make sure first day is Monday)
    // First day is the day of the month - the day of the week
    // last day is the first day + 6
    const first = date.getDate() - date.getDay() + 1

    const firstDayOfWeek = new Date(date.setDate(first))
    
    const firstDay = firstDayOfWeek.toLocaleDateString()
    const lastDay = new Date(date.setDate(date.getDate() + 6)).toLocaleDateString()

    const nextWeekFirstDate = new Date(date.setDate(date.getDate() + 1)).toLocaleDateString()

    const readOnly = isThisWeekReadOnly(firstDay)

    this.state = {
      firstDay,
      lastDay,
      nextWeekFirstDate,
      readOnly,
      type: "Actual",
      projects: [],
      proposals: [],
      selectedProjects: [],
      selectedProposals: [],
      fetchingProjectHours: true,
      fetchingProposalHours: true,
    }
  }

  componentDidMount() {
    this.getWorkloadProjects()
    this.getWorkloadProposals()
  }

  // componentWillUnmount() {
  //   this.cancelSignal.cancel('cancelling api call')
  // }

  componentDidUpdate(prevProps, prevState) {
    const { inputtedHours } = this.props
    if (inputtedHours.length === 0 && prevProps.inputtedHours.length !== 0) {
      this.setSelected()
    }

    const { firstDay } = this.state
    if (firstDay !== prevState.firstDay) {
      this.setState({ readOnly: isThisWeekReadOnly(firstDay) })
    }
  }


  getWorkloadProjects = () => {
    axios({
      method: "GET",
      url: `${getServerURL()}workload-projects/`,
      headers: { "Authorization": "Bearer " + localStorage.getItem("token") },
    })
    .then(response => {
      this.setState({ projects: response.data }, () => {
        this.getEmployeeProjectHours()
      })
    })
    .catch(error => {
      const message = "There was an error getting your projects"
      handleErrorFetchingData(error, message)
    })
  }


  getEmployeeProjectHours = (firstDay=this.state.firstDay, lastDay=this.state.lastDay, loadProjectsOnly=false) => {
    // loadProjectsOnly is a flag to only load the projects from last week and not all of the hours
    this.setState({ fetchingProjectHours: true })

    // Check if there is an axios cancel token for project hours, and if so, cancel the previous request (this is used to handle a case of a user quickly clicking between the next or previous week buttons and running multiple requests for the week's hours, which results in wonky behavior)
    if (typeof this.projectHoursCancelToken !== typeof undefined) {
      this.projectHoursCancelToken.cancel('Cancel project hours get request')
    }

    // Set a new projectHoursCancelToken if one doesn't exist or the current one has been cancelled
    this.projectHoursCancelToken = axios.CancelToken.source()

    axios({
      method: "GET",
      url: `${getServerURL()}workload-tracker/project-hours/`,
      props: {'my_hours': true},
      params: {'first_day': firstDay, 'last_day': lastDay},
      headers: { "Authorization": "Bearer " + localStorage.getItem("token")},
      cancelToken: this.projectHoursCancelToken.token,
    })
    .then(response => {
      // if loadProjectsOnly the user does not want the hours from last week selected, just the projects
      if (loadProjectsOnly) {
        const selectedProjects = this.getLastWeekParentFromHours('projects', response.data.results)
        return this.setState({ selectedProjects })
      }
      if (firstDay === this.state.firstDay) {
        this.props.successGetEmployeeProjectHours(response.data.results)
      } else {
        const hours = this.formatLastWeekHours(response.data.results, 'project')
        this.props.addLastWeekHours(hours)
      }
      this.setSelected()
      if (loadProjectsOnly) {
        this.props.successGetEmployeeProjectHours([])
      }
    })
    .catch(error => {
      if (axios.isCancel(error)) {
        return null
      } else {
        const message = "There was an error getting your hours"
        handleErrorFetchingData(error, message)
      }
    })
    .finally(() => {
      this.setState({ fetchingProjectHours: false })
    })
  }

  getLastWeekParentFromHours = (hourAssociationType, hours) => {
    const singularHourAssociationType = hourAssociationType.slice(0, hourAssociationType.length - 1)
    return this.state[hourAssociationType].filter(element => {
      return hours.find(hour => {
        return hour[singularHourAssociationType] && hour[singularHourAssociationType].url === element.url
      })
    })
  }


  getWorkloadProposals = () => {
    axios({
      method: "GET",
      url: `${getServerURL()}workload-proposals/`,
      headers: { "Authorization": "Bearer " + localStorage.getItem("token") },
    })
    .then(response => {
      this.setState({ proposals: response.data }, () => {
        this.getEmployeeProposalHours()
      })
    })
    .catch(error => {
      const message = "There was an error getting your proposals"
      handleErrorFetchingData(error, message)
    })
  }


  getEmployeeProposalHours = (firstDay=this.state.firstDay, lastDay=this.state.lastDay, loadProposalsOnly=false) => {
    // loadProposalsOnly is a flag to only load the projects from last week and not all of the hours
    this.setState({ fetchingProposalHours: true })

    // Check if there is an axios cancel token for proposal hours, and if so, cancel the previous request (this is used to handle a case of a user quickly clicking between the next or previous week buttons and running multiple requests for the week's hours, which results in wonky behavior)
    if (typeof this.proposalHoursCancelToken !== typeof undefined) {
      this.proposalHoursCancelToken.cancel('Cancel proposal hours get request')
    }

    // Set a new proposalHoursCancelToken if one doesn't exist or the current one has been cancelled
    this.proposalHoursCancelToken = axios.CancelToken.source()

    axios({
      method: "GET",
      url: `${getServerURL()}workload-tracker/proposal-hours/`,
      props: {'my_hours': true},
      params: {'first_day': firstDay, 'last_day': lastDay},
      headers: { "Authorization": "Bearer " + localStorage.getItem("token")},
      cancelToken: this.proposalHoursCancelToken.token,
    })
    .then(response => {
      // if loadProposalsOnly the user does not want the hours from last week selected, just the proposals
      if (loadProposalsOnly) {
        const selectedProposals = this.getLastWeekParentFromHours('proposals', response.data.results)
        return this.setState({ selectedProposals })
      }
      if (firstDay === this.state.firstDay) {
        this.props.successGetEmployeeProposalHours(response.data.results)
      } else {
        const hours = this.formatLastWeekHours(response.data.results, 'proposal')
        this.props.addLastWeekHours(hours)
      }
      this.setSelected()
    })
    .catch(error => {
      if (axios.isCancel(error)) {
        return null
      } else {
        const message = "There was an error getting your hours"
        handleErrorFetchingData(error, message)
      }
    })
    .finally(() => {
      this.setState({ fetchingProposalHours: false })
    })
  }


  loadPreviousWeekHours = (loadProjectsOnly=false) => {
    // When loading last week's hours, get the first and last days and pass them as arguments to the getEmployeeProjectHours funciton
    // First day is the day of the month - the day of the week
    // last day is the first day + 6
    // loadProjectsOnly is a flag to only load the projects from last week and not all of the hours
    const today = new Date()
    const firstDay = (today.getDate() - today.getDay()) - 6
    const weekStart = new Date(today.setDate(firstDay))
    const weekStartString = weekStart.toLocaleDateString()

    const lastDay = (weekStart.getDate()) + 6
    const weekEnd = new Date(weekStart.setDate(lastDay))
    const weekEndString = weekEnd.toLocaleDateString()
    this.getEmployeeProjectHours(weekStartString, weekEndString, loadProjectsOnly)
    this.getEmployeeProposalHours(weekStartString, weekEndString, loadProjectsOnly)
  }


  formatLastWeekHours = (hours, parentElementType) => {
    // When getting last week's hours, this function formats them in the expected format for the reducer
    return hours.map(hour => {
      let date = new Date(hour.date.replace(/-/g, "/"))
      date.setDate(date.getDate() + 7)
      date = new Date(date).toLocaleDateString()
      const pluralParentElementType = parentElementType + 's'
      const parentElement = this.state[pluralParentElementType].find(element => {
        return element.id === hour[parentElementType].id
      })
      return {
        currentUser: this.props.currentUser,
        hours: hour.hours,
        type: hour.type,
        date,
        [parentElementType]: parentElement,
      }
    })
  }


  setWeekToPrevious = () => {
    this.props.resetInputtedHours()
    let firstDayDate = new Date(this.state.firstDay).getDate()

    let firstDay = new Date(this.state.firstDay)
    firstDay.setDate(firstDayDate - 7)
    firstDay = firstDay.toLocaleDateString()

    let lastDayDate = new Date(this.state.lastDay).getDate()

    let lastDay = new Date(this.state.lastDay)
    lastDay.setDate(lastDayDate - 7)
    lastDay = lastDay.toLocaleDateString()

    this.setState({ firstDay, lastDay }, () => {
      this.getEmployeeProjectHours()
      this.getEmployeeProposalHours()
    })
  }


  setWeekToNext = () => {
    this.props.resetInputtedHours()
    let firstDayDate = new Date(this.state.firstDay).getDate()

    let firstDay = new Date(this.state.firstDay)
    firstDay.setDate(firstDayDate + 7)
    firstDay = firstDay.toLocaleDateString()

    let lastDayDate = new Date(this.state.lastDay).getDate()

    let lastDay = new Date(this.state.lastDay)
    lastDay.setDate(lastDayDate + 7)
    lastDay = lastDay.toLocaleDateString()

    this.setState({ firstDay, lastDay }, () => {
      this.getEmployeeProjectHours()
      this.getEmployeeProposalHours()
    })
  }


  setSelected = () => {
    const selectedProjects = this.getSelected('projects', 'projectHours', 'selectedProjects')

    const selectedProposals = this.getSelected('proposals', 'proposalHours', 'selectedProposals')

    this.setState({ selectedProjects, selectedProposals })
  }


  getSelected = (hourAssociationType, hours, stateElement) => {
    // Find project correlating to the employee's hours and set them as the selected proejcts in state

    // takes the hourAssociationType type, ex 'projects' and removes the 's' at the end to get a singular version
    const singularHourAssociationType = hourAssociationType.slice(0, hourAssociationType.length - 1)

    // if there are hours ex this.props.projectHours
    if (this.props[hours]) {
      let selectedFromSavedHours = this.props[hours].map(hour => {

        return this.state[hourAssociationType].find(element => {
          return hour[singularHourAssociationType] && element.url === hour[singularHourAssociationType].url && hour.type === this.state.type
        })
      })

      let selectedFromInputHours = this.props.inputtedHours.map(hour => {
        return this.state[hourAssociationType].find(element => {
          return hour[singularHourAssociationType] && element.url === hour[singularHourAssociationType].url && hour.type === this.state.type
        })
      })

      let selected = [...selectedFromSavedHours, ...selectedFromInputHours]

      selected = [...new Set(selected)]
      selected = selected.filter(Boolean)
      // stateElement is the selected hour association type for the hours ex selectedProjects/selectedProposals
      return selected
    }
  }


  handleProjectSelected = (project) => {
    let { selectedProjects } = this.state
    if (project && !this.state.selectedProjects.includes(project)) {
      selectedProjects = [...selectedProjects, project]
      this.setState({ selectedProjects })
    }
    // this.typeahead_general.getInstance().clear()
    this.typeaheadAssigned.getInstance().clear()
  }


  handleProposalSelected = (proposal) => {
    let { selectedProposals } = this.state
    if (proposal && !this.state.selectedProposals.includes(proposal)) {
      selectedProposals = [...selectedProposals, proposal]
      this.setState({ selectedProposals })
    }
    this.typeaheadProposals.getInstance().clear()
  }


  handleRemove = (element, stateProperty) => {

    const { projectHours, proposalHours, inputtedHours } = this.props

    const allHours = [...projectHours, ...proposalHours, ...inputtedHours ]

    const hoursToRemove = allHours.filter(hour => {
      if (hour.project) {
        return hour.project.id === element.id
      } else if (hour.proposal) {
        return hour.proposal.id === element.id
      } else {
        return false
      }
    })

    hoursToRemove.forEach(hour => {
      const date = new Date(hour.date.replace(/-/g, "/")).toLocaleDateString()
      this.props.addHours({ ...hour, date, hours: 0 })
    })

    let updatedStateProperty = this.state[stateProperty].filter(el => {
      return el.id !== element.id
    })
    this.setState({ [stateProperty]: updatedStateProperty })
  }


  postHours = (hours, hourAssociationType) => {
    const parsedHours = hours.map(hour => {
      return {...hour, hours: Number(hour.hours)}
    })

    return axios({
      method: "POST",
      url: `${getServerURL()}workload-tracker/${hourAssociationType}-hours/`,
      headers: { "Authorization": "Bearer " + localStorage.getItem("token") },
      data: { hours: parsedHours },
    })
  }


  handleSubmit = () => {
    // Separate project and proposal hours and post to the backend.
    // Return a promise for each, and when both resolve display a success message
    const { inputtedHours } = this.props

    let projectHours = inputtedHours.filter(hour => hour.project)
    let proposalHours = inputtedHours.filter(hour => hour.proposal)

    let newHoursToPost

    if (projectHours.length && proposalHours.length) {
      newHoursToPost = [
        this.postHours(projectHours,'project'),
        this.postHours(proposalHours,'proposal')
      ]
    } else if (projectHours.length) {
      newHoursToPost = [this.postHours(projectHours, 'project')]
    } else if (proposalHours.length) {
      newHoursToPost = [this.postHours(proposalHours, 'proposal')]
    } else {
      // In case no hours were inputed do nothing
      return
    }

    swal({
      title: "Saving hours",
      text: "Please do not navigate away from this page.",
      icon: require("../../static/images/spinner.gif"),
      button: false
    })

    Promise.all(newHoursToPost)
    .then(response => {
      swal({
        title: "Success",
        text: "Your hours have been uploaded",
        icon: "success",
        button: "Continue",
      })
      this.props.addNewHours(inputtedHours)
    })
    .catch(error => {
      let message = "There was an error uploading your hours"
      if (error && error.response && error.response.data && error.response.data.message) {
        message = error.response.data.message
      }
      handleErrorFetchingData(error, message)
    })
  }


  changeHourType = (type) => {
    // Toggle type of hours between 'Actual' and 'Projected' and then set the selected projects based on the type
    this.setState({type}, () => this.setSelected())
  }


  resetHours = () => {
    this.props.resetInputtedHours()
    this.setSelected()
  }


  currentWeekIsSelected = () => {
    const { firstDay } = this.state
    const today = new Date()
    let first = today.getDate() - today.getDay() + 1

    let thisWeekFirstDay = new Date(today.setDate(first)).toLocaleDateString()

    if (firstDay !== thisWeekFirstDay) {
      return true
    }

    return false
  }


  resetWeekToCurrentWeek = () => {
    let date = new Date()
    let first = date.getDate() - date.getDay() + 1
    let firstDay = new Date(date.setDate(first)).toLocaleDateString()
    let lastDay = new Date(date.setDate(date.getDate() + 6)).toLocaleDateString()
    this.setState({ firstDay, lastDay })
    this.getEmployeeProjectHours(firstDay, lastDay)
    this.getEmployeeProposalHours(firstDay, lastDay)
  }

  showLoadPreviousHoursButton = () => {
    const { firstDay, fetchingProjectHours, fetchingProposalHours } = this.state
    const { proposalHours, projectHours } = this.props
    const now = new Date()
    
    const firstDayOfThisWeek = now.getDate() - now.getDay() + 1
    const firstDateOfThisWeek = new Date()
    firstDateOfThisWeek.setDate(firstDayOfThisWeek)
    
    return firstDay === firstDateOfThisWeek.toLocaleDateString() && !fetchingProjectHours && !fetchingProposalHours && proposalHours.length === 0 && projectHours.length === 0
  }

  filterProjectOptions = (project) => {
    const { selectedProjects } = this.state
    if (selectedProjects.includes(project)) {
      return false
    }

    if (project.is_general && project.short_id === 'OOO' && project.number === 0) {
      return true
    }

    if (project.is_general && project.short_id !== 'GR') {
      return false
    }

    // Hiding certain GR general projects, but keepking Genesis General and General BD. The < 5 logic is in case any additional general projects are added
    if (project.is_general && project.short_id === 'GR' && (project.number !== 0 && project.number < 5)) {
      return false
    }

    return true
  }


  render () {
    const {
      fetchingProjectHours,
      fetchingProposalHours,
      projects,
      // proposals,
      firstDay,
      lastDay,
      readOnly,
      // nextWeekFirstDate,
      type,
      selectedProjects,
      selectedProposals,
    } = this.state

    return (
      <div id="WorkloadContainer">
        <AuthenticationComponent />
        <Grid>

          {!readOnly && Array.isArray(this.props.inputtedHours) && this.props.inputtedHours.length ?
            <Button
              onClick={this.resetHours}
              style={{ marginRight: '10px' }}
            >
              Reset hours
            </Button>
            :
            null
          }

          {this.showLoadPreviousHoursButton() ?
          <Fragment>
              <Button
                onClick={() => this.loadPreviousWeekHours(true)}
                style={{ marginRight: '10px' }}
              >
                Load Previous Week Projects
              </Button>
              <Button
                onClick={() => this.loadPreviousWeekHours(false)}
              >
                Load Previous Week Hours
              </Button>
          </Fragment>
            :
            null
          }

          {this.currentWeekIsSelected() ?
            <Button
              onClick={this.resetWeekToCurrentWeek}
              style={{marginLeft: '10px'}}
            >
              See Today
            </Button>
            :
            null
          }

          <br />
          <br />

          <WeekPicker
            setWeekToPrevious={this.setWeekToPrevious}
            setWeekToNext={this.setWeekToNext}
            firstDay={firstDay}
          />

          {readOnly && !fetchingProjectHours && !fetchingProposalHours ?
            <Fragment>
              <br />
              <center>
                <Alert bsStyle="danger">
                  <h4>
                    This week has been locked for hour entry.
                  </h4>
                  <p>
                    If you have any questions, please consult the {' '}
                    <a
                      href="https://drive.google.com/drive/folders/1HvvrBuxk0G0O0bbi9OXRh0IZOHS-9cKm"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'underline' }}
                    >
                      PTOR Workload Manual
                    </a> {' '}
                    or contact your line manager
                  </p>
                </Alert>
              </center>
            </Fragment>
            :
            null
          }

          {fetchingProjectHours || fetchingProposalHours ?
            <center>
              <img alt="loader" src={require("../../static/images/spinner.gif")} />
              <h4>Loading</h4>
            </center>
            :
            <Fragment>
              <ButtonToolbar className="button-toolbar">
                <ToggleButtonGroup
                  onChange={this.changeHourType}
                  type="radio"
                  name="options"
                  value={type}
                >
                  <ToggleButton value="Actual">
                    Actual
                  </ToggleButton>
                  {/*<ToggleButton value="Projected">
                    Projected
                  </ToggleButton>*/}
                </ToggleButtonGroup>
              </ButtonToolbar>

              <WorkloadTrackerTable
                handleRemove={this.handleRemove}
                firstDay={firstDay}
                lastDay={lastDay}
                type={type}
                readOnly={readOnly}
                selectedProjects={selectedProjects}
                selectedProposals={selectedProposals}
              />

              {readOnly ?
                null
                :
                <div style={{width:"50%", margin:"auto"}}>
                  <center>
                    <Button bsStyle="primary" onClick={this.handleSubmit}>
                      Submit
                    </Button>
                  </center>
                  <div>
                    Projects
                    <Typeahead
                      id="assigned-projects"
                      paginate={true}
                      clearButton
                      labelKey={project => `${project.short_id}-${project.number}-${project.title}`}
                      placeholder="Projects"
                      onChange={(project) => this.handleProjectSelected(project[0])}
                      options={projects.filter(this.filterProjectOptions)}
                      ref={ (typeaheadAssigned) => this.typeaheadAssigned = typeaheadAssigned }
                    />
                  </div>
                  <br/>
                  <br/>

                  {/* <div>
                    Proposals
                    <Typeahead
                      id="assigned-projects"
                      paginate={true}
                      clearButton
                      labelKey={proposal => proposal.client ? `${proposal.client.name}-${proposal.client.short_code}-${proposal.number} - ${proposal.title}` : `${proposal.number} - ${proposal.title}`}
                      placeholder="Proposals"
                      onChange={(proposal) => this.handleProposalSelected(proposal[0])}
                      options={proposals.filter(proposal => !selectedProposals.includes(proposal))}
                      ref={ (typeaheadProposals) => this.typeaheadProposals = typeaheadProposals }
                    />
                  </div> */}

                </div>
              }

            </Fragment>
          }
        </Grid>
      </div>
    )
  }
}

const mapStateToProps = (state) => {
  return {
		inputtedHours: state.manageHours.inputtedHours,
    projectHours: state.manageHours.projectHours,
    proposalHours: state.manageHours.proposalHours,
    currentUser: state.manageLogin.user,
  }
}

export default connect(mapStateToProps, {resetInputtedHours, successGetEmployeeProjectHours, successGetEmployeeProposalHours, addLastWeekHours, addNewHours, addHours})(WorkloadContainer)



// WEBPACK FOOTER //
// ./src/routes/workload/WorkloadContainer.js